import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react';

const GAP = 4;
const EDGE = 8;

/**
 * A menu anchored to its own button, on top of the native Popover API: the browser puts it in the
 * top layer, light-dismisses it on Escape or an outside click, and returns focus to the button.
 *
 * Placement is computed here rather than with CSS anchor positioning, which Chrome alone supports
 * and which silently collapses to a zero-size box when the anchor sits inside a table.
 */
export function MenuPopover({
  trigger,
  label,
  triggerClassName = 'btn btn-sm',
  panelClassName = 'w-64',
  align = 'end',
  disabled,
  title,
  children,
}: {
  /** Contents of the button. */
  trigger: ReactNode;
  /** Accessible name for the button. */
  label: string;
  triggerClassName?: string;
  panelClassName?: string;
  /** Which edge of the panel lines up with the button. */
  align?: 'start' | 'end';
  disabled?: boolean;
  title?: string;
  /** Panel contents; call `close` to dismiss the menu. */
  children: (close: () => void) => ReactNode;
}) {
  const id = `menu-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const close = useCallback(() => panel.current?.hidePopover?.(), []);

  const place = useCallback(() => {
    const b = button.current;
    const p = panel.current;
    if (!b || !p) return;
    const r = b.getBoundingClientRect();
    const { offsetWidth: w, offsetHeight: h } = p;
    const left = align === 'end' ? r.right - w : r.left;
    // Below the button, flipped above when it would not fit, and always inside the viewport.
    const below = r.bottom + GAP;
    const top = below + h > window.innerHeight - EDGE && r.top - h - GAP > EDGE ? r.top - h - GAP : below;
    p.style.left = `${Math.max(EDGE, Math.min(left, window.innerWidth - w - EDGE))}px`;
    p.style.top = `${Math.max(EDGE, top)}px`;
  }, [align]);

  useEffect(() => {
    const p = panel.current;
    if (!p) return;
    // daisyUI positions .dropdown popovers with anchor positioning; take that over.
    p.style.setProperty('position-area', 'none');
    p.style.setProperty('margin', '0');
    p.style.setProperty('inset', 'auto');

    let open = false;
    // `toggle`, not `beforetoggle`: the panel has no size until the browser has shown it.
    const onToggle = (e: Event) => {
      open = (e as ToggleEvent).newState === 'open';
      if (open) place();
    };
    const reflow = () => open && place();
    p.addEventListener('toggle', onToggle);
    window.addEventListener('resize', reflow);
    // Capture: the menu must follow its button when any scroll container moves.
    window.addEventListener('scroll', reflow, true);
    return () => {
      p.removeEventListener('toggle', onToggle);
      window.removeEventListener('resize', reflow);
      window.removeEventListener('scroll', reflow, true);
    };
  }, [place]);

  return (
    <>
      <button ref={button} type="button" popoverTarget={id} disabled={disabled} title={title} aria-label={label} className={triggerClassName}>
        {trigger}
      </button>
      <div
        popover="auto"
        id={id}
        ref={panel}
        className={`dropdown fixed rounded-box border border-hairline bg-base-100 p-1.5 dr-shadow-lg ${panelClassName}`}
      >
        {children(close)}
      </div>
    </>
  );
}

/** A rule between groups of menu items. */
export function MenuSeparator() {
  return <li role="separator" className="mx-1 my-1 border-t border-hairline" />;
}
